import React, { createContext, useContext, useState, useEffect, useRef, ReactNode } from "react";
import { getMyTeams, getMyPermissions, getPendingInvitations, Team, Invitation, MyPermissionsResponse } from "../services/api/team.service";
import { useUser } from "./UserContext";
import { trackEvent, DASHBOARD_EVENTS } from "../services/analytics";

export interface ArtistInfo {
  artistId: number;
  artistName: string;
  clickhouseArtistId?: string; // ClickHouse artist ID for secondary database queries
  isOwner: boolean;
  teams: Team[];
}

interface ArtistContextType {
  // Artist selection
  currentArtistId: number | null;
  setCurrentArtistId: (artistId: number | null) => void;
  
  // Available artists
  availableArtists: ArtistInfo[];
  loadingArtists: boolean;
  
  // Initialization state
  isInitialized: boolean; // True when artists have been loaded and artist is selected
  
  // Current artist permissions
  currentPermissions: string[];
  currentRoles: string[];
  currentTeamIds: number[];
  loadingPermissions: boolean;
  
  // Teams and invitations
  myTeams: Team[];
  pendingInvitations: Invitation[];
  loadingTeams: boolean;
  loadingInvitations: boolean;
  
  // Artist switching state
  switchingArtist: boolean;
  
  // Actions
  refreshArtists: () => Promise<void>;
  refreshPermissions: () => Promise<void>;
  refreshTeams: () => Promise<void>;
  refreshInvitations: () => Promise<void>;
  
  // Helpers
  hasPermission: (permission: string) => boolean;
  hasAnyPermission: (permissions: string[]) => boolean;
  hasAllPermissions: (permissions: string[]) => boolean;
}

const ArtistContext = createContext<ArtistContextType | undefined>(undefined);

export const ArtistProvider = ({ children }: { children: ReactNode }) => {
  const { userProfile, loading: userLoading } = useUser();
  // Initialize currentArtistId from localStorage on mount to preserve selection across page reloads
  const [currentArtistId, setCurrentArtistIdState] = useState<number | null>(() => {
    try {
      const saved = localStorage.getItem('currentArtistId');
      return saved ? parseInt(saved, 10) : null;
    } catch {
      return null;
    }
  });
  const [availableArtists, setAvailableArtists] = useState<ArtistInfo[]>([]);
  const [loadingArtists, setLoadingArtists] = useState(false);
  
  const [currentPermissions, setCurrentPermissions] = useState<string[]>([]);
  const [currentRoles, setCurrentRoles] = useState<string[]>([]);
  const [currentTeamIds, setCurrentTeamIds] = useState<number[]>([]);
  const [loadingPermissions, setLoadingPermissions] = useState(false);
  
  const [myTeams, setMyTeams] = useState<Team[]>([]);
  const [pendingInvitations, setPendingInvitations] = useState<Invitation[]>([]);
  const [loadingTeams, setLoadingTeams] = useState(false);
  const [loadingInvitations, setLoadingInvitations] = useState(false);
  const [switchingArtist, setSwitchingArtist] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  
  const initializedRef = useRef(false);
  const loadingRef = useRef(false);
  const previousArtistIdRef = useRef<number | null>(null);

  // Custom setter that synchronously updates localStorage
  const setCurrentArtistId = (artistId: number | null) => {
    console.log('[ArtistContext] setCurrentArtistId called with:', artistId);

    // Track artist switch (only if changing from one artist to another)
    if (artistId && currentArtistId && artistId !== currentArtistId) {
      trackEvent(DASHBOARD_EVENTS.ARTIST_SWITCHED);
    }

    // Set state
    setCurrentArtistIdState(artistId);

    // Set localStorage IMMEDIATELY (synchronously) so apiFetch can read it
    if (artistId) {
      const artistIdString = artistId.toString();
      localStorage.setItem('currentArtistId', artistIdString);
      
      // Verify it was set
      const verify = localStorage.getItem('currentArtistId');
      console.log('[ArtistContext] Set currentArtistId to localStorage:', artistIdString, 'Verified:', verify);
      
      if (verify !== artistIdString) {
        console.error('[ArtistContext] ERROR: localStorage verification failed! Expected:', artistIdString, 'Got:', verify);
      }
    } else {
      localStorage.removeItem('currentArtistId');
      console.log('[ArtistContext] Removed currentArtistId from localStorage');
    }
  };

  // Emit team status only after teams are loaded (avoid early empty-state false)
  const emitTeamStatusReady = (artists: ArtistInfo[]) => {
    const hasNonOwnerTeam = artists.some(artist => !artist.isOwner);
    sessionStorage.setItem('hasNonOwnerTeam', hasNonOwnerTeam ? 'true' : 'false');
    const event = new CustomEvent('teamStatusReady', { detail: { hasNonOwnerTeam } });
    window.dispatchEvent(event);
  };

  // Load teams and invitations in parallel after login
  const loadInitialData = async () => {
    // console.log('[ArtistContext] loadInitialData called', {
    //   loadingRef: loadingRef.current,
    //   hasUserProfile: !!userProfile
    // });

    // Only check loadingRef to prevent concurrent calls
    // userLoading and userProfile are already verified in useEffect
    if (loadingRef.current) {
      console.log('[ArtistContext] loadInitialData skipped - already loading');
      return;
    }
    
    if (!userProfile) {
      console.log('[ArtistContext] loadInitialData skipped - no user profile (should not happen)');
      return;
    }
    
    // console.log('[ArtistContext] Starting to load teams and invitations...');
    loadingRef.current = true;
    setLoadingTeams(true);
    setLoadingInvitations(true);
    
    try {
      // Call both APIs in parallel
      // console.log('[ArtistContext] Calling getMyTeams() and getPendingInvitations()...');
      const [teamsResponse, invitationsResponse] = await Promise.all([
        getMyTeams().catch(err => {
          console.error('[ArtistContext] Error loading teams:', err);
          return [];
        }),
        getPendingInvitations().catch(err => {
          console.error('[ArtistContext] Error loading invitations:', err);
          return [];
        })
      ]);
      
      console.log('[ArtistContext] Teams loaded:', teamsResponse?.length || 0, 'teams');
      console.log('[ArtistContext] Invitations loaded:', invitationsResponse?.length || 0, 'invitations');
      
      setMyTeams(teamsResponse);
      setPendingInvitations(invitationsResponse);
      
      // Build available artists from teams
      buildAvailableArtists(teamsResponse);
    } catch (error) {
      console.error('[ArtistContext] Error loading initial data:', error);
    } finally {
      setLoadingTeams(false);
      setLoadingInvitations(false);
      loadingRef.current = false;
      console.log('[ArtistContext] loadInitialData completed');
    }
  };

  // Build list of available artists from teams
  const buildAvailableArtists = (teams: Team[]) => {
    setLoadingArtists(true);
    
    try {
      // Group teams by artist
      const artistMap = new Map<number, ArtistInfo>();
      
      teams.forEach(team => {
        if (!team.artistId) return;
        
        if (!artistMap.has(team.artistId)) {
          artistMap.set(team.artistId, {
            artistId: team.artistId,
            artistName: team.artistName || `Artist ${team.artistId}`,
            clickhouseArtistId: team.clickhouseArtistId, // Extract clickhouseArtistId from first team
            isOwner: team.isOwner || false,
            teams: []
          });
        }
        
        const artist = artistMap.get(team.artistId)!;
        artist.teams.push(team);
        // Update isOwner if any team shows user as owner
        if (team.isOwner) {
          artist.isOwner = true;
        }
        // Update clickhouseArtistId if not set and this team has it
        if (!artist.clickhouseArtistId && team.clickhouseArtistId) {
          artist.clickhouseArtistId = team.clickhouseArtistId;
        }
      });
      
      const artists = Array.from(artistMap.values());
      setAvailableArtists(artists);
      // Inform UserContext once teams are processed
      emitTeamStatusReady(artists);
      
      // Always check localStorage first to restore the previously selected artist on page reload
        const savedArtistId = localStorage.getItem('currentArtistId');
      let shouldSetArtist = false;
      let artistIdToSet: number | null = null;
      
      if (artists.length === 1) {
        // Only one artist available - select it
        if (!currentArtistId || currentArtistId !== artists[0].artistId) {
          shouldSetArtist = true;
          artistIdToSet = artists[0].artistId;
        }
      } else if (artists.length > 1) {
        // Multiple artists available
        if (savedArtistId) {
          // Try to restore from localStorage first (page reload case)
          const artistId = parseInt(savedArtistId, 10);
          if (artists.some(a => a.artistId === artistId)) {
            // Saved artist ID is valid - restore it
            if (!currentArtistId || currentArtistId !== artistId) {
              shouldSetArtist = true;
              artistIdToSet = artistId;
            }
          } else {
            // Saved artist ID is invalid (artist no longer available)
            // Fallback to owner artist or first artist
            const ownerArtist = artists.find(a => a.isOwner);
            const fallbackArtistId = ownerArtist ? ownerArtist.artistId : artists[0].artistId;
            if (!currentArtistId || currentArtistId !== fallbackArtistId) {
              shouldSetArtist = true;
              artistIdToSet = fallbackArtistId;
            }
          }
        } else if (!currentArtistId) {
          // No saved selection and no current selection (first time)
          // Prioritize "My Account" (isOwner === true)
          const ownerArtist = artists.find(a => a.isOwner);
          if (ownerArtist) {
            // Auto-select "My Account" (login user's own artist)
            shouldSetArtist = true;
            artistIdToSet = ownerArtist.artistId;
          } else {
            // No owner artist found, fallback to first artist
            shouldSetArtist = true;
            artistIdToSet = artists[0].artistId;
          }
        }
        // If currentArtistId is already set and valid, keep it (don't override)
        // But we still need to ensure permissions are loaded (handled by useEffect)
      }
      
      // Set artist if needed (this will trigger the useEffect that loads permissions)
      if (shouldSetArtist && artistIdToSet !== null) {
        setCurrentArtistId(artistIdToSet);
      }
      
      // Mark as initialized when artists are loaded
      // If there are artists, also require an artist to be selected
      // If there are no artists, mark as initialized anyway (so UI can show fallback)
      const finalArtistId = currentArtistId || artistIdToSet || localStorage.getItem('currentArtistId');
      if (artists.length > 0 && finalArtistId) {
        setIsInitialized(true);
      } else if (artists.length === 0) {
        // No artists available, but loading is complete - mark as initialized
        setIsInitialized(true);
      }
    } catch (error) {
      console.error('Error building available artists:', error);
    } finally {
      setLoadingArtists(false);
    }
  };

  // Load permissions for current artist
  const loadPermissions = async (isSwitching: boolean = false) => {
    if (!currentArtistId || loadingPermissions) return;
    
    if (isSwitching) {
      setSwitchingArtist(true);
    }
    setLoadingPermissions(true);
    try {
      const permissionsData = await getMyPermissions(currentArtistId);
      setCurrentPermissions(permissionsData.permissions || []);
      setCurrentRoles(permissionsData.roles || []);
      setCurrentTeamIds(permissionsData.teamIds || []);
    } catch (error) {
      console.error('Error loading permissions:', error);
      setCurrentPermissions([]);
      setCurrentRoles([]);
      setCurrentTeamIds([]);
    } finally {
      setLoadingPermissions(false);
      if (isSwitching) {
        // Small delay to ensure all components have time to start refetching
        setTimeout(() => {
          setSwitchingArtist(false);
        }, 500);
      }
    }
  };

  // Initialize after user profile loads
  useEffect(() => {
    // console.log('[ArtistContext] useEffect triggered:', {
    //   userLoading,
    //   hasUserProfile: !!userProfile,
    //   initialized: initializedRef.current,
    //   loadingRef: loadingRef.current
    // });

    if (!userLoading && userProfile && !initializedRef.current) {
      // console.log('[ArtistContext] Initializing - calling loadInitialData()');
      initializedRef.current = true;
      loadInitialData();
    } else {
      // console.log('[ArtistContext] Skipping initialization:', {
      //   reason: !userLoading ? 'userLoading is true' : !userProfile ? 'no userProfile' : 'already initialized'
      // });
    }
  }, [userLoading, userProfile]);

  // Load permissions when artist changes AND artists are available
  // This ensures permissions load only after teams/artists are loaded (important for page reload)
  useEffect(() => {
    if (currentArtistId) {
      // localStorage is already set synchronously in setCurrentArtistId, but ensure it's in sync
      // This is a backup to ensure consistency
      const stored = localStorage.getItem('currentArtistId');
      if (stored !== currentArtistId.toString()) {
        localStorage.setItem('currentArtistId', currentArtistId.toString());
        console.log('[ArtistContext] Synced currentArtistId to localStorage in useEffect:', currentArtistId);
      }
      
      // CRITICAL: Only load permissions if artists are available
      // This prevents loading permissions for an artist that might not be in the available list yet (page reload case)
      if (availableArtists.length > 0) {
        // Verify the currentArtistId is actually in the available artists list
        const isValidArtist = availableArtists.some(a => a.artistId === currentArtistId);
        
        if (isValidArtist) {
          // Mark as initialized when artist is selected and artists are loaded
        setIsInitialized(true);
      
      // Check if this is a switch (not initial load)
      const isSwitching = previousArtistIdRef.current !== null && previousArtistIdRef.current !== currentArtistId;
      previousArtistIdRef.current = currentArtistId;
      loadPermissions(isSwitching);
        } else {
          // Artist from localStorage is not in available list - clear it and wait for buildAvailableArtists to set a valid one
          console.warn('[ArtistContext] currentArtistId from localStorage is not in available artists, clearing it');
          setCurrentArtistId(null);
        }
      } else {
        // Artists not loaded yet - wait for buildAvailableArtists to complete
        // Don't load permissions yet, and don't set isInitialized
        console.log('[ArtistContext] Waiting for artists to load before loading permissions for artist:', currentArtistId);
      }
    } else {
      setCurrentPermissions([]);
      setCurrentRoles([]);
      setCurrentTeamIds([]);
      setIsInitialized(false);
      previousArtistIdRef.current = null;
      // Ensure localStorage is cleared if artistId is null
      if (localStorage.getItem('currentArtistId')) {
        localStorage.removeItem('currentArtistId');
        console.log('[ArtistContext] Cleared currentArtistId from localStorage');
      }
    }
  }, [currentArtistId, availableArtists]);

  // Refresh functions
  const refreshArtists = async () => {
    await refreshTeams();
  };

  const refreshTeams = async () => {
    setLoadingTeams(true);
    try {
      const teams = await getMyTeams();
      setMyTeams(teams);
      buildAvailableArtists(teams);
    } catch (error) {
      console.error('Error refreshing teams:', error);
    } finally {
      setLoadingTeams(false);
    }
  };

  const refreshInvitations = async () => {
    setLoadingInvitations(true);
    try {
      const invitations = await getPendingInvitations();
      setPendingInvitations(invitations);
    } catch (error) {
      console.error('Error refreshing invitations:', error);
    } finally {
      setLoadingInvitations(false);
    }
  };

  const refreshPermissions = async () => {
    if (currentArtistId) {
      await loadPermissions();
    }
  };

  // Permission helpers
  const hasPermission = (permission: string): boolean => {
    return currentPermissions.includes(permission);
  };

  const hasAnyPermission = (permissions: string[]): boolean => {
    return permissions.some(p => hasPermission(p));
  };

  const hasAllPermissions = (permissions: string[]): boolean => {
    return permissions.every(p => hasPermission(p));
  };

  return (
    <ArtistContext.Provider
      value={{
        currentArtistId,
        setCurrentArtistId,
        availableArtists,
        loadingArtists,
        isInitialized,
        currentPermissions,
        currentRoles,
        currentTeamIds,
        loadingPermissions,
        myTeams,
        pendingInvitations,
        loadingTeams,
        loadingInvitations,
        switchingArtist,
        refreshArtists,
        refreshPermissions,
        refreshTeams,
        refreshInvitations,
        hasPermission,
        hasAnyPermission,
        hasAllPermissions,
      }}
    >
      {children}
    </ArtistContext.Provider>
  );
};

export const useArtist = () => {
  const context = useContext(ArtistContext);
  if (context === undefined) {
    throw new Error("useArtist must be used within an ArtistProvider");
  }
  return context;
};

